import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { EMP } from "../constants/testIds";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { SeparateEmployeeDialog, ResetLinkDialog } from "../components/EmployeeActions";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { MagnifyingGlass, Plus, UploadSimple, X, Warning } from "@phosphor-icons/react";

const empty = {
  employee_code: "",
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  date_of_birth: "",
  gender: "",
  department: "",
  designation: "",
  grade: "",
  band: "",
  level: "",
  date_of_joining: "",
  location: "",
  role: "employee",
  initial_password: "Welcome@123",
  // New personal + emergency
  blood_group: "",
  emergency_contact_name: "",
  emergency_contact_phone: "",
  emergency_contact_relation: "",
  uan: "",
  permanent_address: "",
  current_address: "",
  passport_photo_path: "",
  // Optional monthly CTC for auto-salary-structure
  monthly_ctc: "",
  // Optional named template — if empty, we use tenant's default template or 40/20/40 fallback
  salary_template_id: "",
};

const DEPARTMENTS = ["Engineering", "Design", "Sales", "Human Resources", "Finance", "Operations", "Marketing"];
const ROLES = ["employee", "manager", "hr", "company_admin"];
const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];
const RELATIONS = ["Spouse", "Parent", "Sibling", "Child", "Friend", "Other"];

export default function Employees() {
  const { user, tenantId } = useAuth();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [dept, setDept] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [photoUploading, setPhotoUploading] = useState(false);
  const photoInputRef = useRef(null);

  const canManage = ["super_admin", "company_admin", "hr"].includes(user?.role);
  const [structure, setStructure] = useState({ grades: [], bands: [] });
  const [salaryTemplates, setSalaryTemplates] = useState([]);

  const load = () => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (dept) params.department = dept;
    if (status) params.status = status;
    if (user?.role === "super_admin" && tenantId) params.tenant_id = tenantId;
    api.get("/employees", { params })
      .then(({ data }) => setRows(data))
      .finally(() => setLoading(false));
  };

  const loadStructure = () => {
    const params = user?.role === "super_admin" && tenantId ? { tenant_id: tenantId } : {};
    api.get("/admin/grade-structure", { params })
      .then(({ data }) => setStructure({ grades: data.grades || [], bands: data.bands || [] }))
      .catch(() => setStructure({ grades: [], bands: [] }));
  };

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, dept, status, tenantId]);

  useEffect(() => { loadStructure(); /* eslint-disable-next-line */ }, [tenantId]);

  useEffect(() => {
    const params = user?.role === "super_admin" && tenantId ? { tenant_id: tenantId } : {};
    api.get("/salary-templates", { params })
      .then(({ data }) => setSalaryTemplates(data || []))
      .catch(() => setSalaryTemplates([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const pickGrade = (gradeCode) => {
    if (gradeCode === "__unset") {
      setForm({ ...form, grade: "", band: form.band, level: "" });
      return;
    }
    const g = structure.grades.find((x) => x.grade === gradeCode);
    if (!g) { setForm({ ...form, grade: gradeCode }); return; }
    setForm({ ...form, grade: g.grade, band: g.band || form.band, level: g.level ?? form.level });
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Photo must be under 5 MB");
      return;
    }
    setPhotoUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((prev) => ({ ...prev, passport_photo_path: data.path || data.storage_path || data.url }));
      toast.success("Photo uploaded");
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setPhotoUploading(false);
  };

  const submit = async () => {
    setSaving(true);
    setErr("");
    try {
      const payload = { ...form, create_login: true };
      if (!payload.email) delete payload.email;
      ["date_of_birth", "gender"].forEach((k) => { if (!payload[k]) delete payload[k]; });
      if (payload.level === "" || payload.level == null) payload.level = null;
      else payload.level = parseInt(payload.level);
      if (payload.monthly_ctc === "" || payload.monthly_ctc == null) delete payload.monthly_ctc;
      else payload.monthly_ctc = parseFloat(payload.monthly_ctc);
      if (user?.role === "super_admin" && tenantId) payload.tenant_id = tenantId;
      const { data } = await api.post("/employees", payload);
      toast.success("Employee onboarded" + (form.monthly_ctc ? " · Salary structure auto-created" : ""));
      if (data.credentials && !data.credentials.email_sent) setCreds({ ...data.credentials, name: `${form.first_name} ${form.last_name}` });
      setOpen(false);
      setForm(empty);
      load();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const [creds, setCreds] = useState(null);
  const [resetInfo, setResetInfo] = useState(null);
  const [separating, setSeparating] = useState(null);
  const resetPw = async (r) => {
    try {
      const { data } = await api.post(`/employees/${r.id}/reset-password`);
      setResetInfo(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const reinstate = async (r) => {
    if (!window.confirm(`Reinstate ${r.first_name} ${r.last_name} as an active employee and re-enable login?`)) return;
    try { await api.post(`/employees/${r.id}/reinstate`); toast.success("Employee reinstated"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (r) => {
    if (!window.confirm(`Delete ${r.first_name} ${r.last_name}? This removes their record, login and salary structure. This cannot be undone.`)) return;
    try { await api.delete(`/employees/${r.id}`); toast.success("Employee deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-emerald-700">Directory</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Employees</h1>
          <p className="text-sm text-zinc-500 mt-2">{rows.length} people · Update roles, departments and reporting lines.</p>
        </div>
        <Dialog open={!!creds} onOpenChange={(v) => !v && setCreds(null)}>
          <DialogContent className="max-w-md" data-testid="emp-credentials-dialog">
            <DialogHeader><DialogTitle className="font-display text-2xl">Share login details</DialogTitle></DialogHeader>
            <div className="text-sm text-zinc-600">{creds?.email_error ? "The welcome email could not be delivered." : "No email ID on file."} Share these credentials with <b>{creds?.name}</b> by phone / WhatsApp{creds?.phone ? ` (${creds.phone})` : ""}.</div>
            <div className="border border-zinc-200 rounded-sm p-3 font-mono-plex text-xs space-y-1 bg-zinc-50">
              <div>Login ID: <b data-testid="cred-login-id">{creds?.login_id}</b></div>
              <div>Temporary password: <b data-testid="cred-password">{creds?.password}</b></div>
              <div className="break-all">Set-password link: {creds?.activation_link}</div>
            </div>
            <DialogFooter>
              <Button variant="outline" className="rounded-sm" onClick={() => { navigator.clipboard?.writeText(`Login ID: ${creds.login_id}\nPassword: ${creds.password}\nSet your password: ${creds.activation_link}`); toast.success("Copied"); }} data-testid="cred-copy">Copy</Button>
              <Button className="btn-zenith rounded-sm" onClick={() => setCreds(null)}>Done</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {canManage && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid={EMP.createBtn} className="btn-zenith rounded-sm">
                <Plus size={16} className="mr-1.5" /> New employee
              </Button>
            </DialogTrigger>
            <DialogContent data-testid={EMP.createDialog} className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">Onboard a new employee</DialogTitle>
              </DialogHeader>

              {/* Section: Identity */}
              <SectionHeading>Identity</SectionHeading>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Employee code" v={form.employee_code} on={(v) => setForm({ ...form, employee_code: v })} tid="emp-code-input" required />
                <Field label="Role" render={() => (
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="emp-role-select" className="rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                  </Select>
                )} />
                <Field label="First name" v={form.first_name} on={(v) => setForm({ ...form, first_name: v })} tid="emp-fn-input" required />
                <Field label="Last name" v={form.last_name} on={(v) => setForm({ ...form, last_name: v })} tid="emp-ln-input" required />
                <Field label="Work email (optional)" v={form.email} on={(v) => setForm({ ...form, email: v })} tid="emp-email-input" type="email" placeholder="Leave blank to share login by phone" />
                <Field label="Mobile number" v={form.phone} on={(v) => setForm({ ...form, phone: v })} tid="emp-phone-input" placeholder="+91 98765 43210" />
              </div>

              {/* Section: Role + Department */}
              <SectionHeading>Role &amp; department</SectionHeading>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Department" render={() => (
                  <Select value={form.department} onValueChange={(v) => setForm({ ...form, department: v })}>
                    <SelectTrigger data-testid="emp-dept-select" className="rounded-sm"><SelectValue placeholder="Select department" /></SelectTrigger>
                    <SelectContent>{DEPARTMENTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                  </Select>
                )} />
                <Field label="Designation" v={form.designation} on={(v) => setForm({ ...form, designation: v })} tid="emp-desig-input" />
                {structure.grades.length > 0 ? (
                  <>
                    <Field label="Grade" render={() => (
                      <Select value={form.grade || "__unset"} onValueChange={pickGrade}>
                        <SelectTrigger className="rounded-sm" data-testid="emp-grade-select"><SelectValue placeholder="Select grade" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__unset">— Unassigned —</SelectItem>
                          {structure.grades.map((g) => (
                            <SelectItem key={g.grade} value={g.grade}>{g.grade} · {g.band} · {g.title_example || "—"}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )} />
                    <Field label="Band" render={() => (
                      <div className="rounded-sm border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm text-zinc-700" data-testid="emp-band-readonly">
                        {form.band ? (
                          <span className="flex items-center gap-2">
                            <Badge variant="outline" className="rounded-sm text-[10px]">{form.band}</Badge>
                            <span className="text-[10px] text-zinc-500">auto from grade</span>
                          </span>
                        ) : (
                          <span className="text-zinc-400 italic text-xs">Pick a grade to auto-fill</span>
                        )}
                      </div>
                    )} />
                    {form.grade && (() => {
                      const g = structure.grades.find((x) => x.grade === form.grade);
                      if (!g) return null;
                      return (
                        <div className="col-span-2 -mt-2 text-[10px] text-zinc-500 font-mono-plex" data-testid="emp-grade-hint">
                          Suggested CTC band: <b>₹{g.min_ctc_lpa} LPA</b> — <b>₹{g.max_ctc_lpa} LPA</b>
                          {g.title_example && <> · Typical title: <b>{g.title_example}</b></>}
                        </div>
                      );
                    })()}
                  </>
                ) : (
                  <>
                    <Field label="Band" render={() => (
                      <Select value={form.band || "__unset"} onValueChange={(v) => setForm({ ...form, band: v === "__unset" ? "" : v })}>
                        <SelectTrigger className="rounded-sm" data-testid="emp-band-select"><SelectValue placeholder="Select band" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__unset">— Unassigned —</SelectItem>
                          {["Individual Contributor", "Senior IC", "Manager", "Senior Manager", "Director", "VP", "C-Suite"].map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    )} />
                    <Field label="Grade" v={form.grade} on={(v) => setForm({ ...form, grade: v })} tid="emp-grade-input" />
                  </>
                )}
                <Field label="Level (1-15)" v={form.level} on={(v) => setForm({ ...form, level: v })} tid="emp-level-input" type="number" />
                <Field label="Date of joining" v={form.date_of_joining} on={(v) => setForm({ ...form, date_of_joining: v })} tid="emp-doj-input" type="date" />
                <Field label="Date of birth" v={form.date_of_birth} on={(v) => setForm({ ...form, date_of_birth: v })} tid="emp-dob-input" type="date" />
                <Field label="Gender" render={() => (
                  <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                    <SelectTrigger data-testid="emp-gender-select" className="rounded-sm"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{["male", "female", "other"].map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                  </Select>
                )} />
                <Field label="Branch / Location" v={form.location} on={(v) => setForm({ ...form, location: v })} tid="emp-location-input" placeholder="Bengaluru · Mumbai · Remote…" />
              </div>

              {/* Section: Personal + Emergency */}
              <SectionHeading>Personal &amp; emergency</SectionHeading>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Blood group" render={() => (
                  <Select value={form.blood_group || "__unset"} onValueChange={(v) => setForm({ ...form, blood_group: v === "__unset" ? "" : v })}>
                    <SelectTrigger className="rounded-sm" data-testid="emp-blood-group-select"><SelectValue placeholder="Select blood group" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__unset">— Unknown —</SelectItem>
                      {BLOOD_GROUPS.map((bg) => <SelectItem key={bg} value={bg}>{bg}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )} />
                <Field label="UAN (PF Universal Account No.)" v={form.uan} on={(v) => setForm({ ...form, uan: v })} tid="emp-uan-input" placeholder="12-digit UAN" />
                <Field label="Emergency contact name" v={form.emergency_contact_name} on={(v) => setForm({ ...form, emergency_contact_name: v })} tid="emp-ec-name-input" />
                <Field label="Emergency contact number" v={form.emergency_contact_phone} on={(v) => setForm({ ...form, emergency_contact_phone: v })} tid="emp-ec-phone-input" placeholder="+91 98765 43210" />
                <Field label="Emergency contact relation" render={() => (
                  <Select value={form.emergency_contact_relation || "__unset"} onValueChange={(v) => setForm({ ...form, emergency_contact_relation: v === "__unset" ? "" : v })}>
                    <SelectTrigger className="rounded-sm" data-testid="emp-ec-relation-select"><SelectValue placeholder="Select relation" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__unset">— Unspecified —</SelectItem>
                      {RELATIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )} />
                <div />
                <div className="col-span-2">
                  <Label className="text-xs">Permanent address</Label>
                  <Textarea rows={2} value={form.permanent_address} onChange={(e) => setForm({ ...form, permanent_address: e.target.value })}
                    placeholder="Line 1, Line 2, City, State, PIN" data-testid="emp-perm-address-input"
                    className="mt-1 rounded-sm" />
                </div>
                <div className="col-span-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">Current address</Label>
                    <button type="button" onClick={() => setForm({ ...form, current_address: form.permanent_address })}
                      className="text-[10px] text-emerald-700 hover:underline" data-testid="emp-copy-perm-address">
                      Copy from permanent
                    </button>
                  </div>
                  <Textarea rows={2} value={form.current_address} onChange={(e) => setForm({ ...form, current_address: e.target.value })}
                    placeholder="Line 1, Line 2, City, State, PIN" data-testid="emp-curr-address-input"
                    className="mt-1 rounded-sm" />
                </div>
              </div>

              {/* Section: Passport photo */}
              <SectionHeading>Passport-size photograph</SectionHeading>
              <div className="flex items-center gap-4">
                <div className="w-24 h-32 border-2 border-dashed border-zinc-300 rounded-sm bg-zinc-50 flex items-center justify-center overflow-hidden"
                  data-testid="emp-photo-preview">
                  {form.passport_photo_path ? (
                    <img src={form.passport_photo_path} alt="passport" className="w-full h-full object-cover" />
                  ) : (
                    <div className="text-[10px] text-zinc-400 text-center px-2">Photo<br />preview</div>
                  )}
                </div>
                <div className="flex-1">
                  <input ref={photoInputRef} type="file" accept="image/*" className="hidden"
                    onChange={(e) => uploadPhoto(e.target.files?.[0])} data-testid="emp-photo-file-input" />
                  <Button variant="outline" onClick={() => photoInputRef.current?.click()}
                    disabled={photoUploading} className="rounded-sm" data-testid="emp-photo-upload-btn">
                    <UploadSimple size={14} className="mr-1.5" />
                    {photoUploading ? "Uploading…" : (form.passport_photo_path ? "Replace photo" : "Upload photo")}
                  </Button>
                  {form.passport_photo_path && (
                    <Button variant="ghost" onClick={() => setForm({ ...form, passport_photo_path: "" })}
                      className="rounded-sm ml-2 text-rose-700 hover:bg-rose-50" data-testid="emp-photo-remove-btn">
                      <X size={12} className="mr-1" /> Remove
                    </Button>
                  )}
                  <div className="text-[10px] text-zinc-500 mt-2">JPG or PNG, max 5 MB. Ideally 35×45 mm at 300 DPI.</div>
                </div>
              </div>

              {/* Section: Salary Structure */}
              <SectionHeading>Salary structure (optional)</SectionHeading>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Monthly CTC (₹)" v={form.monthly_ctc} on={(v) => setForm({ ...form, monthly_ctc: v })}
                  tid="emp-monthly-ctc-input" type="number" placeholder="e.g. 100000" />
                <Field label="Salary template" render={() => (
                  <Select value={form.salary_template_id || "__default"}
                    onValueChange={(v) => setForm({ ...form, salary_template_id: v === "__default" ? "" : v })}>
                    <SelectTrigger className="rounded-sm" data-testid="emp-salary-template-select">
                      <SelectValue placeholder="Tenant default (40/20/40)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__default">Tenant default (Basic 40 · HRA 20 · Special 40)</SelectItem>
                      {salaryTemplates.map((t) => (
                        <SelectItem key={t.id} value={t.id}>{t.name}{t.is_default ? " · default" : ""}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )} />
                <CtcSlabWarning ctc={parseFloat(form.monthly_ctc) || 0}
                  grade={form.grade} grades={structure.grades} />
                <SalaryPreview ctc={parseFloat(form.monthly_ctc) || 0}
                  templateId={form.salary_template_id}
                  templates={salaryTemplates} />
                <div className="col-span-2 text-[11px] text-zinc-500 leading-relaxed">
                  If Monthly CTC is provided, a salary structure is auto-created using the selected template. Manage templates under <b>Admin → Salary templates</b>. You can fine-tune the resulting structure later under <b>Payroll ops</b>.
                </div>
              </div>

              {/* Login */}
              <SectionHeading>Login credentials</SectionHeading>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Initial password" v={form.initial_password} on={(v) => setForm({ ...form, initial_password: v })} tid="emp-password-input" />
              </div>

              {err && <div className="text-sm text-red-600 border border-red-200 bg-red-50 px-3 py-2 rounded-sm">{err}</div>}
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
                <Button onClick={submit} disabled={saving} data-testid={EMP.saveBtn} className="btn-zenith rounded-sm">
                  {saving ? "Saving…" : "Create & send login"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Filters */}
      <div className="border border-zinc-200 bg-white p-3 rounded-sm flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            data-testid={EMP.searchInput}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, code or email…"
            className="pl-9 rounded-sm border-zinc-300 focus-visible:ring-1 focus-visible:ring-emerald-700"
          />
        </div>
        <Select value={dept} onValueChange={(v) => setDept(v === "__all" ? "" : v)}>
          <SelectTrigger className="w-[200px] rounded-sm" data-testid="emp-filter-dept"><SelectValue placeholder="All departments" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">All departments</SelectItem>
            {DEPARTMENTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={(v) => setStatus(v === "__all" ? "" : v)}>
          <SelectTrigger className="w-[160px] rounded-sm" data-testid="emp-filter-status"><SelectValue placeholder="All status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">All status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
            <SelectItem value="exited">Exited / separated</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div data-testid={EMP.list} className="overflow-x-auto border border-zinc-200 rounded-sm">
        <table className="hr-table">
          <thead>
            <tr>
              <th>Code</th>
              <th>Name</th>
              <th>Department</th>
              <th>Designation</th>
              <th>Grade</th>
              <th>Location</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="text-center py-8 text-zinc-500 text-sm">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-8 text-zinc-500 text-sm">No employees match your filters.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} data-testid={EMP.row(r.id)}>
                <td className="font-mono-plex text-xs">{r.employee_code}</td>
                <td>
                  <div className="font-medium text-zinc-900">{r.first_name} {r.last_name}</div>
                  <div className="text-xs text-zinc-500">{r.email}</div>
                </td>
                <td>{r.department || "—"}</td>
                <td>{r.designation || "—"}</td>
                <td>{r.grade || "—"}</td>
                <td>{r.location || "—"}</td>
                <td>
                  <Badge className={`rounded-sm ${r.status === "active" ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : r.status === "exited" ? "bg-red-50 text-red-800 border border-red-200" : "bg-zinc-100 text-zinc-700 border border-zinc-200"}`} variant="outline" data-testid={`emp-status-${r.id}`}>
                    {r.status}
                  </Badge>
                  {r.status === "exited" && r.last_working_date && <div className="text-[10px] text-zinc-500 mt-0.5">LWD {r.last_working_date}</div>}
                </td>
                <td>
                  <div className="flex items-center gap-3">
                    <Link className="text-xs text-emerald-700 hover:underline" to={`/app/employees/${r.id}`} data-testid={`emp-open-${r.id}`}>Open →</Link>
                    {canManage && r.id !== user?.employee_id && (
                      <>
                        <button className="text-xs text-blue-600 hover:underline" onClick={() => resetPw(r)} data-testid={`emp-reset-pw-${r.id}`}>Reset password</button>
                        {["exited", "inactive"].includes(r.status)
                          ? <>
                            <Link className="text-xs text-amber-700 hover:underline" to={`/app/employees/${r.id}/exit`} data-testid={`emp-exit-checklist-${r.id}`}>Exit checklist</Link>
                            <button className="text-xs text-emerald-700 hover:underline" onClick={() => reinstate(r)} data-testid={`emp-reinstate-${r.id}`}>Reinstate</button>
                          </>
                          : <button className="text-xs text-amber-700 hover:underline" onClick={() => setSeparating(r)} data-testid={`emp-separate-${r.id}`}>Mark exited</button>}
                        <button className="text-xs text-red-600 hover:underline" onClick={() => del(r)} data-testid={`emp-delete-${r.id}`}>Delete</button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <SeparateEmployeeDialog emp={separating} onClose={() => setSeparating(null)} onDone={load} />
      <ResetLinkDialog data={resetInfo} onClose={() => setResetInfo(null)} />
    </div>
  );
}

function SectionHeading({ children }) {
  return (
    <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold mt-4 mb-1 border-b border-zinc-100 pb-1">{children}</div>
  );
}

function Field({ label, v, on, tid, type = "text", render, required, placeholder }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}{required ? " *" : ""}</Label>
      {render ? render() : (
        <Input value={v || ""} onChange={(e) => on(e.target.value)} type={type} placeholder={placeholder} data-testid={tid} className="rounded-sm border-zinc-300 focus-visible:ring-1 focus-visible:ring-emerald-700" />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// CTC slab warning — cross-checks entered CTC against the picked grade's band.
// ---------------------------------------------------------------------------
function CtcSlabWarning({ ctc, grade, grades }) {
  if (!ctc || ctc <= 0 || !grade || !grades?.length) return null;
  const g = grades.find((x) => x.grade === grade);
  if (!g) return null;
  const min = Number(g.min_ctc_lpa);
  const max = Number(g.max_ctc_lpa);
  if (!min || !max) return null;
  const annualLPA = (ctc * 12) / 100000;
  if (annualLPA >= min && annualLPA <= max) {
    // Within band — subtle emerald confirmation
    return (
      <div className="col-span-2 text-[11px] text-emerald-700 flex items-center gap-1.5" data-testid="ctc-slab-ok">
        <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-600" />
        CTC {annualLPA.toFixed(2)} LPA is within the suggested band for grade <b>{g.grade}</b> (₹{min}–{max} LPA).
      </div>
    );
  }
  const isBelow = annualLPA < min;
  const suggestedGrade = grades.find((x) =>
    Number(x.min_ctc_lpa) && Number(x.max_ctc_lpa)
    && annualLPA >= Number(x.min_ctc_lpa) && annualLPA <= Number(x.max_ctc_lpa)
  );
  return (
    <div className="col-span-2 border border-amber-300 bg-amber-50 text-amber-900 rounded-sm px-3 py-2 flex items-start gap-2 text-xs"
      data-testid="ctc-slab-warning">
      <Warning size={16} weight="fill" className="text-amber-600 flex-none mt-0.5" />
      <div>
        Entered CTC <b>{annualLPA.toFixed(2)} LPA</b> is {isBelow ? "below" : "above"} the suggested band for grade <b>{g.grade}</b> (₹{min}–{max} LPA).
        {suggestedGrade ? (
          <> Consider grade <b data-testid="ctc-slab-suggested-grade">{suggestedGrade.grade}</b> ({suggestedGrade.band}, ₹{suggestedGrade.min_ctc_lpa}–{suggestedGrade.max_ctc_lpa} LPA) instead.</>
        ) : (
          <> {isBelow ? "You may want a lower grade" : "You may want a higher grade"} — no configured grade matches this CTC.</>
        )}
        {' '}You can still proceed with the current selection.
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Live salary preview — mirrors backend salary_from_template() exactly.
// ---------------------------------------------------------------------------
function SalaryPreview({ ctc, templateId, templates }) {
  if (!ctc || ctc <= 0) return null;
  const chosen = templateId ? templates.find((t) => t.id === templateId) : null;
  const fallbackDefault = templates.find((t) => t.is_default);
  const t = chosen || fallbackDefault || {
    name: "Default (40/20/40)",
    basic_percent: 40, hra_percent: 20, special_allowance_percent: 40,
    conveyance_percent: 0, medical_allowance_percent: 0,
    employee_pf_percent_of_basic: 12, employer_pf_percent_of_basic: 12, pf_cap: 1800,
    professional_tax: 200, professional_tax_min_ctc: 25000, tds_percent: 0,
  };
  const basic = Math.round(ctc * (t.basic_percent || 40) / 100);
  const hra = Math.round(ctc * (t.hra_percent || 20) / 100);
  const conv = Math.round(ctc * (t.conveyance_percent || 0) / 100);
  const med = Math.round(ctc * (t.medical_allowance_percent || 0) / 100);
  const special = Math.round(ctc - basic - hra - conv - med);
  const pfE = Math.min(Math.round(basic * (t.employee_pf_percent_of_basic || 12) / 100), t.pf_cap || 1800);
  const pfR = Math.min(Math.round(basic * (t.employer_pf_percent_of_basic || 12) / 100), t.pf_cap || 1800);
  const pt = ctc > (t.professional_tax_min_ctc || 0) ? (t.professional_tax || 0) : 0;
  const totalDed = pfE + pt;
  const netPay = ctc - totalDed;
  const ctcCost = ctc + pfR;
  const fmt = (n) => `₹${Math.round(n).toLocaleString("en-IN")}`;
  const isDefaultBucket = !chosen && !fallbackDefault;
  return (
    <div className="col-span-2 border border-emerald-200 bg-emerald-50/60 rounded-sm p-3" data-testid="salary-preview">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="text-[10px] uppercase tracking-widest text-emerald-800 font-semibold">
          Live preview · {t.name || "Default"}
          {isDefaultBucket && <span className="ml-2 opacity-70">(fallback)</span>}
        </div>
        <div className="text-sm text-emerald-900 font-mono-plex">
          Net pay <b data-testid="salary-preview-net">{fmt(netPay)}</b>/mo
        </div>
      </div>
      <div className="grid grid-cols-3 md:grid-cols-6 gap-2 text-xs pb-2 border-b border-emerald-200/70 font-mono-plex">
        <PreviewCell label="Basic" value={basic} tid="preview-basic" />
        <PreviewCell label="HRA" value={hra} tid="preview-hra" />
        <PreviewCell label="Special" value={special} tid="preview-special" />
        {conv > 0 && <PreviewCell label="Conveyance" value={conv} />}
        {med > 0 && <PreviewCell label="Medical" value={med} />}
        <PreviewCell label="− Emp PF" value={pfE} negative />
        {pt > 0 && <PreviewCell label="− PT" value={pt} negative />}
      </div>
      <div className="text-[10px] text-emerald-800 mt-1.5 flex items-center justify-between flex-wrap gap-2">
        <span>Cost to company: <b>{fmt(ctcCost)}/mo</b> · incl. employer PF {fmt(pfR)}</span>
        <span className="opacity-70">Fine-tune under Payroll ops after creation.</span>
      </div>
    </div>
  );
}

function PreviewCell({ label, value, negative, tid }) {
  const fmt = (n) => `₹${Math.abs(Math.round(n)).toLocaleString("en-IN")}`;
  return (
    <div data-testid={tid}>
      <div className="text-[9px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={negative ? "text-rose-700 font-medium" : "text-emerald-900 font-semibold"}>
        {fmt(value)}
      </div>
    </div>
  );
}
